// Hallazgos de inspección (F3a): automáticos (desde un "no cumple") o manuales; descripción, foto, nivel de riesgo,
// acción correctiva, responsable (trabajador o cargo), fecha límite y estado (abierto, en proceso, cerrado; "vencido" se
// calcula). Cerrar exige evidencia (foto o archivo), como las acciones obligatorias del plan: el botón no se habilita
// sin ella y la base lo rechaza igual (trigger de M41).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ACEPTA_EVIDENCIA, tamanoLegible } from '../../lib/pe/pe-evidencias';
import {
  borrarArchivo, borrarHallazgo, cargarArchivos, crearHallazgos, guardarHallazgo, HallazgoEditable, subirArchivo, urlArchivo,
} from '../../lib/inspecciones/insp-datos';
import { estadoHallazgo, fechaEc, FUENTE_PLAZOS, isoLocal, plazoSugerido } from '../../lib/inspecciones/insp-logica';
import type { EstadoHallazgo, FilaArchivo, FilaHallazgo, FilaInspeccion, Riesgo, SedeMin, TrabajadorMin } from '../../lib/inspecciones/insp-tipos';
import { ETIQUETA_ESTADO_HALLAZGO, ETIQUETA_RIESGO, RIESGOS } from '../../lib/inspecciones/insp-tipos';
import SelectBuscable from '../../components/SelectBuscable';
import { Campo, ModalConfirmar } from '../pe/ComponentesPE';

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export interface PropsHallazgos {
  companyId: string;
  hallazgos: FilaHallazgo[];
  inspecciones: FilaInspeccion[];
  sedes: SedeMin[];
  varias: boolean;
  trabajadores: TrabajadorMin[];
  puedeEditar: boolean;
  puedeBorrar: boolean;
  usuarioId: string | null;
  onCambio: () => void;
  hoy: Date;
}

interface Form extends HallazgoEditable { site_id: string; inspection_id: string }
const formVacio = (hoy: Date, sede: string): Form => ({
  description: '', risk_level: 'medio', corrective_action: '', responsible_worker_id: null, responsible_role: '', due_date: plazoSugerido('medio', isoLocal(hoy)),
  site_id: sede, inspection_id: '',
});

export default function HallazgosInsp(p: PropsHallazgos) {
  const [filtro, setFiltro] = useState<EstadoHallazgo | 'pendientes' | ''>('pendientes');
  const [abierto, setAbierto] = useState<string | 'nuevo' | null>(null);
  const [form, setForm] = useState<Form>(() => formVacio(p.hoy, ''));
  const [archivos, setArchivos] = useState<FilaArchivo[]>([]);
  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [borrar, setBorrar] = useState(false);

  const matriz = p.sedes.find(s => s.is_main)?.id ?? '';
  const actual = abierto && abierto !== 'nuevo' ? p.hallazgos.find(h => h.id === abierto) ?? null : null;
  const lista = useMemo(() => p.hallazgos.filter(h => {
    const e = estadoHallazgo(h, p.hoy);
    return !filtro || (filtro === 'pendientes' ? e !== 'cerrado' : e === filtro);
  }), [p.hallazgos, filtro, p.hoy]);
  const nombreInsp = useCallback((id: string | null) => {
    const i = id ? p.inspecciones.find(x => x.id === id) : null;
    return i ? `${i.template_name} (${fechaEc(i.performed_date ?? i.scheduled_date)})` : 'Registro manual';
  }, [p.inspecciones]);

  useEffect(() => {
    if (!actual) { setArchivos([]); return; }
    cargarArchivos(p.companyId, { hallazgos: [actual.id] }).then(setArchivos).catch(e => setError(msg(e)));
  }, [actual, p.companyId]);

  function abrir(h: FilaHallazgo | 'nuevo') {
    setError(''); setOk(''); setNota('');
    if (h === 'nuevo') { setForm(formVacio(p.hoy, matriz)); setAbierto('nuevo'); return; }
    setForm({ description: h.description, risk_level: h.risk_level, corrective_action: h.corrective_action ?? '', responsible_worker_id: h.responsible_worker_id,
      responsible_role: h.responsible_role ?? '', due_date: h.due_date, site_id: h.site_id, inspection_id: h.inspection_id ?? '' });
    setNota(h.closure_note ?? '');
    setAbierto(h.id);
  }

  async function ejecutar(accion: () => Promise<void>) {
    setOcupado(true); setError(''); setOk('');
    try { await accion(); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  const datos = (): HallazgoEditable => ({
    description: form.description.trim(), risk_level: form.risk_level, corrective_action: form.corrective_action?.trim() || null,
    responsible_worker_id: form.responsible_worker_id || null, responsible_role: form.responsible_role?.trim() || null, due_date: form.due_date || null,
  });

  const guardar = () => ejecutar(async () => {
    if (!form.description.trim()) throw new Error('Describe el hallazgo.');
    if (abierto === 'nuevo') {
      const insp = form.inspection_id ? p.inspecciones.find(i => i.id === form.inspection_id) : null;
      const [h] = await crearHallazgos(p.companyId, [{ ...datos(), site_id: insp?.site_id ?? (form.site_id || null), inspection_id: insp?.id ?? null,
        item_key: null, origin: 'manual' }], p.usuarioId);
      p.onCambio(); setAbierto(h?.id ?? null); setOk('Hallazgo registrado. Puedes agregarle una foto.');
    } else if (actual) {
      await guardarHallazgo(actual.id, datos(), p.usuarioId);
      p.onCambio(); setOk('Hallazgo guardado.');
    }
  });

  const cambiarEstado = (status: FilaHallazgo['status']) => ejecutar(async () => {
    if (!actual) return;
    await guardarHallazgo(actual.id, { status, closure_note: status === 'cerrado' ? nota.trim() || null : actual.closure_note }, p.usuarioId);
    p.onCambio();
    setOk(status === 'cerrado' ? 'Hallazgo cerrado con su evidencia.' : status === 'en_proceso' ? 'Hallazgo en proceso.' : 'Hallazgo reabierto.');
  });

  const subir = (kind: 'foto_hallazgo' | 'evidencia_cierre', archivo: File | null | undefined) => {
    if (!archivo || !actual) return;
    ejecutar(async () => {
      const f = await subirArchivo(p.companyId, { kind, hallazgoId: actual.id }, archivo, p.usuarioId);
      setArchivos(prev => [...prev, f]);
    });
  };
  const quitar = (f: FilaArchivo) => ejecutar(async () => { await borrarArchivo(f); setArchivos(prev => prev.filter(x => x.id !== f.id)); });
  const ver = (f: FilaArchivo) => ejecutar(async () => {
    const a = document.createElement('a'); a.href = await urlArchivo(f.storage_path); a.target = '_blank'; a.rel = 'noopener noreferrer';
    document.body.appendChild(a); a.click(); a.remove();
  });
  const confirmarBorrar = () => ejecutar(async () => {
    if (!actual) return;
    await borrarHallazgo(actual.id); setBorrar(false); setAbierto(null); p.onCambio(); setOk('Hallazgo borrado.');
  });

  const evidencias = archivos.filter(a => a.kind === 'evidencia_cierre');
  const ro = !p.puedeEditar || (!!actual && actual.status === 'cerrado');
  const listaArchivos = (kind: 'foto_hallazgo' | 'evidencia_cierre', titulo: string) => {
    const fs = archivos.filter(a => a.kind === kind);
    return (
      <div className="pe2-evidencias">
        <b>{titulo}</b>
        {fs.length === 0 ? <span className="pe2-nota">Ninguno.</span> : (
          <ul className="pe2-evidencias-lista">{fs.map(f => (
            <li key={f.id}><span className="pe2-evidencia-nombre">{f.file_name} · {tamanoLegible(f.size_bytes)}</span>
              <span className="pe2-evidencia-botones">
                <button type="button" className="pe2-btn" onClick={() => ver(f)}>Ver</button>
                {p.puedeBorrar && !(kind === 'evidencia_cierre' && actual?.status === 'cerrado') && (
                  <button type="button" className="pe2-btn peligro" aria-label={`Quitar ${f.file_name}`} onClick={() => quitar(f)} disabled={ocupado}>Quitar</button>
                )}
              </span></li>
          ))}</ul>
        )}
        {p.puedeEditar && actual && actual.status !== 'cerrado' && (
          <label className="pe2-btn pe2-btn-archivo">
            {kind === 'evidencia_cierre' ? 'Subir evidencia de la corrección' : 'Agregar foto del hallazgo'}
            <input type="file" accept={ACEPTA_EVIDENCIA} capture="environment" disabled={ocupado}
              aria-label={kind === 'evidencia_cierre' ? 'Evidencia de la corrección' : 'Foto del hallazgo'} onChange={e => { subir(kind, e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        )}
      </div>
    );
  };

  return (
    <section className="pe2-card" aria-label="Hallazgos">
      <div className="pe2-cabecera">
        <h3>Hallazgos</h3>
        {p.puedeEditar && <button type="button" className="pe2-btn primario" onClick={() => abrir('nuevo')}>Nuevo hallazgo</button>}
      </div>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {ok && <p className="pe2-ok" role="status">{ok}</p>}

      {abierto && (
        <div className="pe2-card pe2-sub-card" role="region" aria-label={abierto === 'nuevo' ? 'Nuevo hallazgo' : 'Hallazgo'}>
          {actual && (
            <div className="pe2-chips">
              <span className={`insp-chip ${estadoHallazgo(actual, p.hoy)}`}>{ETIQUETA_ESTADO_HALLAZGO[estadoHallazgo(actual, p.hoy)]}</span>
              <span className="pe2-nota">{actual.origin === 'automatico' ? 'Automático' : 'Manual'} · {nombreInsp(actual.inspection_id)}</span>
            </div>
          )}
          <div className="pe2-grid">
            <Campo etiqueta="Descripción del hallazgo">
              <textarea value={form.description} disabled={ro || ocupado} onChange={e => setForm({ ...form, description: e.target.value })} />
            </Campo>
            <Campo etiqueta="Acción correctiva">
              <textarea value={form.corrective_action ?? ''} disabled={ro || ocupado} onChange={e => setForm({ ...form, corrective_action: e.target.value })} />
            </Campo>
            <Campo etiqueta="Nivel de riesgo" nota={FUENTE_PLAZOS}>
              <select value={form.risk_level} disabled={ro || ocupado}
                onChange={e => { const r = e.target.value as Riesgo; setForm({ ...form, risk_level: r, due_date: abierto === 'nuevo' ? plazoSugerido(r, isoLocal(p.hoy)) : form.due_date }); }}>
                {RIESGOS.map(r => <option key={r} value={r}>{ETIQUETA_RIESGO[r]}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Fecha límite">
              <input type="date" value={form.due_date ?? ''} disabled={ro || ocupado} onChange={e => setForm({ ...form, due_date: e.target.value || null })} />
            </Campo>
            <Campo etiqueta="Responsable (trabajador)">
              <SelectBuscable value={form.responsible_worker_id ?? ''} disabled={ro || ocupado} vacio="Sin trabajador"
                onChange={v => setForm({ ...form, responsible_worker_id: v || null })} opciones={p.trabajadores.map(t => ({ value: t.id, label: t.full_name }))} />
            </Campo>
            <Campo etiqueta="Responsable (cargo)">
              <input type="text" value={form.responsible_role ?? ''} disabled={ro || ocupado} onChange={e => setForm({ ...form, responsible_role: e.target.value })} />
            </Campo>
            {abierto === 'nuevo' && (
              <Campo etiqueta="Inspección (opcional)">
                <SelectBuscable value={form.inspection_id} disabled={ocupado} vacio="Sin inspección (registro manual)" onChange={v => setForm({ ...form, inspection_id: v })}
                  opciones={p.inspecciones.filter(i => i.status !== 'cancelada').map(i => ({ value: i.id, label: nombreInsp(i.id) }))} />
              </Campo>
            )}
            {abierto === 'nuevo' && p.varias && !form.inspection_id && (
              <Campo etiqueta="Sede">
                <SelectBuscable obligatorio value={form.site_id} disabled={ocupado} onChange={v => setForm({ ...form, site_id: v })}
                  opciones={p.sedes.filter(s => s.active).map(s => ({ value: s.id, label: s.name }))} />
              </Campo>
            )}
          </div>
          {actual && listaArchivos('foto_hallazgo', 'Fotos del hallazgo')}
          {actual && listaArchivos('evidencia_cierre', 'Evidencia de la corrección (obligatoria para cerrar)')}
          {actual && actual.status !== 'cerrado' && p.puedeEditar && (
            <Campo etiqueta="Nota de cierre (opcional)">
              <input type="text" value={nota} disabled={ocupado} onChange={e => setNota(e.target.value)} />
            </Campo>
          )}
          {actual?.status === 'cerrado' && <p className="pe2-nota">Cerrado el {new Date(actual.closed_at as string).toLocaleDateString('es-EC')}{actual.closure_note ? `: ${actual.closure_note}` : ''}.</p>}
          <div className="pe2-acciones">
            <button type="button" className="pe2-btn" onClick={() => setAbierto(null)} disabled={ocupado}>Cerrar panel</button>
            {p.puedeBorrar && actual && <button type="button" className="pe2-btn peligro" onClick={() => setBorrar(true)} disabled={ocupado}>Borrar</button>}
            {p.puedeEditar && actual && actual.status === 'abierto' && <button type="button" className="pe2-btn" onClick={() => cambiarEstado('en_proceso')} disabled={ocupado}>Marcar en proceso</button>}
            {p.puedeEditar && actual && actual.status === 'cerrado' && <button type="button" className="pe2-btn" onClick={() => cambiarEstado('en_proceso')} disabled={ocupado}>Reabrir hallazgo</button>}
            {p.puedeEditar && actual && actual.status !== 'cerrado' && (
              <button type="button" className="pe2-btn" onClick={() => cambiarEstado('cerrado')} disabled={ocupado || evidencias.length === 0}
                title={evidencias.length === 0 ? 'Sube la evidencia de la corrección para cerrar' : undefined}>Cerrar hallazgo</button>
            )}
            {!ro && <button type="button" className="pe2-btn primario" onClick={guardar} disabled={ocupado}>{abierto === 'nuevo' ? 'Registrar hallazgo' : 'Guardar cambios'}</button>}
          </div>
          {actual && actual.status !== 'cerrado' && evidencias.length === 0 && p.puedeEditar && <p className="pe2-nota">Para cerrar el hallazgo sube la evidencia de la corrección (foto o archivo).</p>}
        </div>
      )}

      <Campo etiqueta="Mostrar">
        <select value={filtro} onChange={e => setFiltro(e.target.value as typeof filtro)}>
          <option value="pendientes">Sin cerrar</option>
          <option value="">Todos</option>
          {(['abierto', 'en_proceso', 'vencido', 'cerrado'] as EstadoHallazgo[]).map(e => <option key={e} value={e}>{ETIQUETA_ESTADO_HALLAZGO[e]}</option>)}
        </select>
      </Campo>
      {lista.length === 0 ? <p className="pe2-nota">No hay hallazgos {filtro === 'pendientes' ? 'sin cerrar' : 'con ese estado'}.</p> : (
        <div className="pe2-tarjetas">
          {lista.map(h => {
            const e = estadoHallazgo(h, p.hoy);
            const sede = p.varias ? p.sedes.find(s => s.id === h.site_id)?.name : null;
            return (
              <div key={h.id} className="pe2-tarjeta">
                <span className={`insp-chip ${e}`}>{ETIQUETA_ESTADO_HALLAZGO[e]}</span>
                <b>{h.description}</b>
                <span className="pe2-nota">Riesgo {ETIQUETA_RIESGO[h.risk_level].toLowerCase()} · fecha límite {fechaEc(h.due_date)}{sede ? ` · ${sede}` : ''}</span>
                <span className="pe2-nota">{nombreInsp(h.inspection_id)}</span>
                <button type="button" className="pe2-btn" aria-label={`Abrir hallazgo: ${h.description}`} onClick={() => abrir(h)}>Abrir</button>
              </div>
            );
          })}
        </div>
      )}
      <ModalConfirmar abierto={borrar} titulo="Borrar el hallazgo" mensaje="Se borra el hallazgo con sus fotos y evidencias. No se puede deshacer." ocupado={ocupado}
        onConfirmar={confirmarBorrar} onCancelar={() => setBorrar(false)} />
    </section>
  );
}
